import React from "react";
import { useAppSelector } from "../../../../store";
import { formatCurrency } from "../../../../utils/formatting";

const WeaponsFooterSummary: React.FC = () => {
  const weaponMounts =
    useAppSelector((state) => state.shipBuilder.weaponMounts) || [];

  // Totals
  const totalMounts = weaponMounts.length;
  const totalWeapons = weaponMounts.reduce(
    (sum, mount) => sum + mount.weapons.length,
    0,
  );

  // Weapon type summary (e.g., Missile Rack x3)
  const weaponTypeCounts: Record<string, number> = {};
  weaponMounts.forEach((mount) => {
    mount.weapons.forEach((weapon) => {
      weaponTypeCounts[weapon.name] = (weaponTypeCounts[weapon.name] || 0) + 1;
    });
  });
  const weaponTypeSummary = Object.entries(weaponTypeCounts)
    .map(([name, count]) => `${name} x${count}`)
    .join(", ");

  // Ammo totals
  let totalAmmoTons = 0;
  let totalAmmoCost = 0;
  let totalWeaponPower = 0;
  weaponMounts.forEach((mount) => {
    mount.weapons.forEach((weapon) => {
      if (weapon.ammo && weapon.extraReloads) {
        totalAmmoTons += weapon.ammo.tonsPerReload * weapon.extraReloads;
        totalAmmoCost += weapon.ammo.costPerReload * weapon.extraReloads;
      }
      totalWeaponPower += weapon.power || 0;
    });
  });

  // Defensive systems summary
  const defensiveSystems =
    useAppSelector((state) => state.shipBuilder.defensiveSystems) || [];
  const defensiveNames = defensiveSystems.map((sys) => sys.name).join(", ");
  const totalDefensiveCost = defensiveSystems.reduce(
    (sum, sys) => {
      const quantity = sys.quantity || 1;
      return sum + ((sys.cost || 0) * quantity);
    },
    0,
  );
  const totalDefensiveWeight = defensiveSystems.reduce(
    (sum, sys) => {
      const quantity = sys.quantity || 1;
      return sum + ((sys.weight || 0) * quantity);
    },
    0,
  );
  const totalDefensivePower = defensiveSystems.reduce(
    (sum, sys) => {
      const quantity = sys.quantity || 1;
      return sum + ((sys.power || 0) * quantity);
    },
    0,
  );

  // Totals for cost/weight (use mount totals which include mount base + weapon weight per Traveller rules)
  let totalWeaponCost = 0;
  let totalWeaponWeight = 0;
  weaponMounts.forEach((mount) => {
    totalWeaponCost += mount.cost || 0;
    totalWeaponWeight += mount.tons || 0;
  });

  // Calculate totals for new summary layout
  const totalWeight = totalWeaponWeight + totalAmmoTons + totalDefensiveWeight;
  const totalCost = totalWeaponCost + totalAmmoCost + totalDefensiveCost;
  const totalPower = totalWeaponPower + totalDefensivePower;

  return (
    <div className="bg-gray-800 text-white p-4 shadow-lg fixed bottom-0 left-0 right-0 z-10">
      <div className="w-full grid grid-cols-[1fr_auto] items-center">
        {/* Left: Breakdown, left-aligned block */}
        <div className="flex-grow min-w-0 text-base font-medium text-left truncate pl-8">
          <div>
            <span>
              <span className="text-gray-400">Mounts:</span>{" "}
              <span className="font-bold">{totalMounts}</span>
            </span>
            <span className="text-gray-600 font-bold text-lg mx-2">|</span>
            <span>
              <span className="text-gray-400">Weapons:</span>{" "}
              <span className="font-bold">{totalWeapons}</span>
            </span>
          </div>
          {weaponTypeSummary && (
            <div className="mt-1 text-sm text-white truncate">
              {weaponTypeSummary}
            </div>
          )}
          {defensiveSystems.length > 0 && (
            <div className="mt-1 text-sm text-white truncate">
              <span className="text-gray-400">Defensive:</span> {defensiveNames}
            </div>
          )}
        </div>
        {/* Right: Three vertical summary columns */}
        <div className="flex flex-row items-stretch gap-x-4 pr-8">
          {/* Volume Column */}
          <div className="flex flex-col items-end justify-center bg-gray-900 rounded-md px-4 py-2 min-w-[140px]">
            <span className="text-gray-400 text-xs mb-1">
              Total Consumed Volume
            </span>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Weapons</span>
              <span className="text-blue-400 font-extrabold ml-2">
                {totalWeaponWeight.toFixed(2)} tons
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Ammo</span>
              <span className="text-blue-400 font-extrabold ml-2">
                {totalAmmoTons.toFixed(2)} tons
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Defence</span>
              <span className="text-blue-400 font-extrabold ml-2">
                {totalDefensiveWeight.toFixed(2)} tons
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-base mt-1 border-t border-gray-700 pt-1">
              <span className="text-white font-bold">Total</span>
              <span className="text-blue-300 font-extrabold ml-2">
                {totalWeight.toFixed(2)} tons
              </span>
            </div>
          </div>
          {/* Divider */}
          <span className="text-gray-700 font-bold text-lg self-stretch flex items-center">
            |
          </span>
          {/* Cost Column */}
          <div className="flex flex-col items-end justify-center bg-gray-900 rounded-md px-4 py-2 min-w-[140px]">
            <span className="text-gray-400 text-xs mb-1">Total Cost</span>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Weapons</span>
              <span className="text-green-400 font-extrabold ml-2">
                {formatCurrency(totalWeaponCost)}
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Ammo</span>
              <span className="text-green-400 font-extrabold ml-2">
                {formatCurrency(totalAmmoCost)}
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Defence</span>
              <span className="text-green-400 font-extrabold ml-2">
                {formatCurrency(totalDefensiveCost)}
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-base mt-1 border-t border-gray-700 pt-1">
              <span className="text-white font-bold">Total</span>
              <span className="text-green-300 font-extrabold ml-2">
                {formatCurrency(totalCost)}
              </span>
            </div>
          </div>
          {/* Divider */}
          <span className="text-gray-700 font-bold text-lg self-stretch flex items-center">
            |
          </span>
          {/* Power Column */}
          <div className="flex flex-col items-end justify-center bg-gray-900 rounded-md px-4 py-2 min-w-[140px]">
            <span className="text-gray-400 text-xs mb-1">
              Total Power Consumption
            </span>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Weapons</span>
              <span className="text-yellow-300 font-extrabold ml-2">
                {totalWeaponPower} power
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-sm">
              <span className="text-gray-400">Defence</span>
              <span className="text-yellow-300 font-extrabold ml-2">
                {totalDefensivePower} power
              </span>
            </div>
            <div className="flex flex-row w-full justify-between text-base mt-1 border-t border-gray-700 pt-1">
              <span className="text-white font-bold">Total</span>
              <span className="text-yellow-200 font-extrabold ml-2">
                {totalPower} power
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeaponsFooterSummary;
